import { Injectable } from '@angular/core';
import { IoConfigurationFeatureCodes } from '@omni/classes/authentication/user.class';
import {
  DEFAULT_BANDS,
  GoalColorBand,
  GoalColorConfig,
  DARKEN_STEP,
  MAX_DARKEN_STEPS,
  MIN_TEXT_CONTRAST,
  ResolvedGoalStyle,
  SUPPORTED_SCHEMA_VERSION,
} from '@omni/config/call-plan/call-plan-goal-color.model';
import { AuthenticationService } from '@omni/services/authentication.service';
/**
 * Colour maths is local rather than imported from color-derivation.util: that utility
 * belongs to the calendar-colours feature and does not exist on every release branch, and
 * this service must apply identically on all of them.
 */
const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

function isValidHex(value: unknown): value is string {
  return typeof value === 'string' && HEX.test(value.trim());
}

function hexToRgb(hex: string): { r: number, g: number, b: number } | null {
  if (!isValidHex(hex)) return null;
  let h = hex.trim().slice(1);
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgbToHex({ r, g, b }: { r: number, g: number, b: number }): string {
  const hex = (v: number) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0');
  return `#${hex(r)}${hex(g)}${hex(b)}`.toUpperCase();
}

/** WCAG 2.x relative luminance. */
function luminance({ r, g, b }: { r: number, g: number, b: number }): number {
  const channel = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** Contrast of a colour against white, which is what these numbers are drawn on. */
function contrastOnWhite(rgb: { r: number, g: number, b: number }): number {
  return 1.05 / (luminance(rgb) + 0.05);
}

/** Org-level records are matched by name; BU-level records carry a feature code. */
const ORG_CONFIG_NAME = 'CallPlan.GoalColors.Configuration';

/**
 * Resolves the colour of a call plan completion percentage. Single source for every
 * surface — list rows, group headers and the detail pane — so the three cannot drift
 * apart again. Always returns a style; the shipped bands are just the config we ship.
 */
@Injectable({ providedIn: 'root' })
export class CallPlanGoalColorService {
  constructor(private readonly authService: AuthenticationService) {}

  private bands: readonly GoalColorBand[] = DEFAULT_BANDS;
  private lastRaw: string | null = null;
  private scannedConfigs: unknown[] | null = null;

  /** Memoised per percentage — a long list resolves the same handful of values. */
  private readonly cache = new Map<number, ResolvedGoalStyle | null>();

  /**
   * Null when there is nothing to measure, or when the percentage matches no band.
   * Unmatched means no colour at all rather than a fallback colour — that is what keeps
   * the shipped configuration quiet below the goal.
   */
  resolve(percentage: number | null): ResolvedGoalStyle | null {
    this.load();
    if (typeof percentage !== 'number' || !Number.isFinite(percentage)) return null;
    if (this.cache.has(percentage)) return this.cache.get(percentage);

    // First match wins, so overlapping bands resolve predictably rather than by accident.
    const band = this.bands.find(b =>
      percentage >= (b.from ?? Number.NEGATIVE_INFINITY) &&
      percentage <= (b.to ?? Number.POSITIVE_INFINITY),
    );
    const resolved = band ? this.style(band.color, band.label) : null;
    this.cache.set(percentage, resolved);
    return resolved;
  }

  /**
   * The ngStyle object for a percentage. Lives here rather than in each component so the
   * list and the detail pane cannot drift apart again.
   */
  /**
   * ngStyle for a percentage: the band's colour as text, or nothing when no band matches.
   * Text rather than a filled chip — every surface here already reads as a number, and a
   * fill on each one crowds the list.
   */
  cssFor(percentage: number | null): { [key: string]: string } {
    const style = this.resolve(percentage);
    return style ? { 'color': style.ink } : {};
  }

  /** Alias kept for callers that read as tinting text, such as the KPI tiles. */
  textColorFor(percentage: number | null): { [key: string]: string } {
    return this.cssFor(percentage);
  }

  /**
   * Solid fill for a progress bar, or null to leave it at its default. The configured hue
   * rather than the chip tint — a 3px bar needs the saturation to register at all.
   */
  barColorFor(percentage: number | null): string | null {
    return this.resolve(percentage)?.fill ?? null;
  }

  /**
   * The configured hue as text, darkened only when it would be too faint to read. Scaling
   * the channels keeps the hue recognisable, so an admin still sees the colour they chose.
   */
  private style(fill: string, label?: string): ResolvedGoalStyle {
    let rgb = hexToRgb(fill);
    if (!rgb) return { fill, ink: fill, label };
    for (let i = 0; i < MAX_DARKEN_STEPS && contrastOnWhite(rgb) < MIN_TEXT_CONTRAST; i++) {
      rgb = { r: rgb.r * DARKEN_STEP, g: rgb.g * DARKEN_STEP, b: rgb.b * DARKEN_STEP };
    }
    return { fill, ink: rgbToHex(rgb), label };
  }

  /** Re-parses only when the underlying record changes, so this is cheap per row. */
  private load(): void {
    const configs = this.authService.user?.ioConfigurations;
    if (configs !== this.scannedConfigs) {
      this.scannedConfigs = configs ?? null;
      const record = (configs || []).find((c: any) =>
        c?.featureCode === IoConfigurationFeatureCodes.CALL_PLAN_GOAL_COLORS ||
        c?.configName === ORG_CONFIG_NAME,
      ) as any;
      const raw = typeof record?.configValue === 'string' && record.configValue.trim().length
        ? record.configValue
        : null;
      if (raw !== this.lastRaw) {
        this.lastRaw = raw;
        this.bands = this.parse(raw);
        this.cache.clear();
      }
    }
  }

  /** Returns the shipped bands for anything unusable, having said why. */
  private parse(raw: string | null): readonly GoalColorBand[] {
    if (!raw) return DEFAULT_BANDS;

    let parsed: GoalColorConfig;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      console.error('[CallPlanGoalColorService] configValue is not valid JSON; using shipped bands.', error);
      return DEFAULT_BANDS;
    }
    if (!parsed || typeof parsed !== 'object') return DEFAULT_BANDS;

    // Present but unrecognised means this build does not understand the shape, so ignore
    // the record rather than half-applying it.
    if (parsed.schemaVersion !== undefined && parsed.schemaVersion !== SUPPORTED_SCHEMA_VERSION) {
      console.warn(
        `[CallPlanGoalColorService] record has unsupported schemaVersion ${parsed.schemaVersion} ` +
        `(this build supports ${SUPPORTED_SCHEMA_VERSION}); using shipped bands.`,
      );
      return DEFAULT_BANDS;
    }

    const bands = (parsed.bands || []).filter(b => b && isValidHex(b.color));
    if (!bands.length) {
      console.warn('[CallPlanGoalColorService] record defines no usable bands; using shipped bands.');
      return DEFAULT_BANDS;
    }
    return bands;
  }
}
